import { useCallback, useContext, useState } from 'react'
import { AppContext } from '../../../App'
import { MultiStrikeChartTypes, setMultiStrikeChartType } from '../../../reducer/multiStrikeChart.reducer'
import { useDispatch, useSelector } from 'react-redux'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { DataUpdateRequestType } from '../../../util/chart'
import { updateMultiStrikeChartConfig } from '../../../reducer/chart.reducer'
import { Menu, MenuItem, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { selectMultiStrikeChartConfigChartType, selectMultiStrikeChartConfigCumOi } from '../../../selectors/chart'
import { selectMultiStrikeChartType, selectMultiStrikeCumOi } from '../../../selectors/multiStrikeChart'
import { useStyles } from './styles'
import { ExtraSmallButton } from '../../../components/custom'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../../components/adminUserRestrictions/Restrictions.constants'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'

type Props = {
    viewKey: string
    isWidget: boolean
    compact?: boolean
}

const chartTypeToName = {
    price: 'Price',
    iv: 'IV',
    oi: 'OI'
}

const MultiStrikeChartTypeMenu = ({ viewKey, isWidget, compact = false }: Props) => {
    const appContext = useContext(AppContext)
    const dispatch = useDispatch()
    const { classes } = useStyles()
    const { user: { hasAccess } } = useUser()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const cumOi = useSelector(isWidget ? selectMultiStrikeChartConfigCumOi(viewKey) : selectMultiStrikeCumOi)

    const handleChartTypeChange = useCallback(
        (e: any, value: string) => {
            if (value) {
                const chartType = value === 'oi' && cumOi ? 'cumOi' : (value as MultiStrikeChartTypes)
                
                if ((chartType === 'oi' || chartType === 'cumOi') && !hasAccess(FeatureCategory.MULTI_STRIKE, FeatureSubCategory.OI_CHARTS)) {
                    dispatch(setFeatureRestrictionDialog({
                        open: true,
                        feature: 'OI Charts',
                        featureRestrictionCode:"MULTI_STRIKE_OI_CHARTS_MULTISTRIKE",
                        group: Group.MULTI_STRIKE
                    }))
                    return
                }
                
                if (isWidget) {
                    dispatch(updateMultiStrikeChartConfig({ viewKey, config: { chartType } }))
                } else {
                    dispatch(setMultiStrikeChartType(chartType))
                }
                AnalyticsManager.logEvent('Chart Type Changed', { type: chartType, source: 'multistrike' })
            }
        },
        [appContext, dispatch, isWidget, viewKey, cumOi, hasAccess]
    )

    return !compact ? (
        <ToggleButtonGroup
            id={'mulstrike-price-options'}
            className={classes.item}
            value={chartType}
            exclusive
            size="small"
            onChange={handleChartTypeChange}
            aria-label="text alignment"
            sx={{
                textWrap: 'nowrap',
                height: '35px',
                '.MuiButtonBase-root': {
                    borderColor: (theme) => theme.palette.background.default
                },
                ml: isWidget ? '0 !important' : '8px !important'
            }}
        >
            <ToggleButton value="iv" aria-label="centered">
                Imp. Vol
            </ToggleButton>
            <ToggleButton selected={chartType === 'oi' || chartType === 'cumOi'} value="oi" aria-label="right aligned">
                Open Int.
            </ToggleButton>
            <ToggleButton value="price" aria-label="left aligned">
                Price
            </ToggleButton>
        </ToggleButtonGroup>
    ) : (
        <>
            <ExtraSmallButton color="inherit" sx={{ margin: 0, padding: 0, fontSize:"13px" }} onClick={(e) => setAnchorEl(e.currentTarget)}>
                {chartType !== 'cumOi' && chartTypeToName[chartType]}
            </ExtraSmallButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={compact && !!anchorEl}
                onClose={() => setAnchorEl(undefined)}
                MenuListProps={{
                    'aria-labelledby': 'basic-button'
                }}
            >
                <MenuItem onClick={(e) => handleChartTypeChange(e, 'iv')}>IV</MenuItem>
                <MenuItem selected={chartType === 'oi' || chartType === 'cumOi'} onClick={(e) => handleChartTypeChange(e, 'oi')}>OI</MenuItem>
                <MenuItem onClick={(e) => handleChartTypeChange(e, 'price')}>Price</MenuItem>
            </Menu>
        </>
    )
}

export default MultiStrikeChartTypeMenu
